import { useCallback, useState } from 'react';
import { Typography, Button, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import {
	Application,
	LocationState,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { InterviewSection } from './InterviewSection';
import { useInterviewLandingPage } from './useInterviewLandingPage';
import {
	ANSWERS_REVIEW_CONTEXT,
	APPLICANT_ADDITIONAL_DETAILS,
	APPLICANT_CONTEXT,
	APPROVER_RECOMMENDATION,
	CARE_TEAM_RECOMMENDATION,
	SCREENING_DASHBOARD,
	FAMILY_CONTEXT,
	FUNCTIONARY_HOME,
	OTHER_REFERENCE_CONTEXT,
	PRECEPTOR_CONTEXT,
	PROPOSER_RECOMMENDATION,
	SKIP_ALL_INTERVIEWS,
	VIEW_ANSWERS,
	ZC_RECOMMENDATION,
	CENTRAL_TEAM_APPLICATIONS,
	VIEW_APPLICANT_INTERVIEW,
	VIEW_PRECEPTOR_INTERVIEW,
	VIEW_FAMILY_MEMBER_INTERVIEW,
	VIEW_OTHER_REFERENCE_INTERVIEW,
	VIEW_CARE_TEAM_APPLICANT_ANSWERS,
	VIEW_CARE_TEAM_APPLICANT_INTERVIEW_ANSWERS,
	VIEW_CARE_TEAM_FAMILY_ANSWERS,
} from '../../../shared/Routes';
import isEmpty from 'lodash/isEmpty';
import { useBatchId } from '../../../hooks/useBatchId';
import {
	ALLOWED_ROLES,
	isApplicationReadOnly,
	isProposerSkippedToEnd,
} from '../../../shared/Utils';
import { useApplicationStatusTranslation } from '../../../hooks/useApplicationStatusTranslation';
import {
	ApplicationStatus,
	FROM_CTD,
	FROM_SCREENING_DASHBOARD,
} from '../../../shared/Constants';
import { ApplicantDetailsTable } from '../ApplicantDetailsTable';
import Icon from '../../../components/ui-components/Icon';
import RightArrowCircle from '../../../assets/images/right-arrow-circle.svg';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import LeftArrow from '../../../assets/images/arrow-left.svg';
import { useLocation } from 'react-router-dom';
import { CommentsModal } from './CommentsModal';
import useUserSession from '../../../hooks/useUserSession';
import { useFunctionaryHome } from '../useFunctionaryHome';
import { resetApplicationsListCache } from '../ApplicationsList/useApplicationsList';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface LandingPageProps {
	application: Partial<Application>;
	onReviewAnswersClick: () => void;
	onInterviewCandidateClick: () => void;
	onInterviewPreceptorClick: () => void;
	onInterviewFamilyMemberClick: () => void;
	onInterviewOtherReferenceClick: () => void;
	onProposerRecommendationClick: () => void;
	onCareTeamRecommendationClick: () => void;
	onZCRecommendationClick: () => void;
	onApproverRecommendationClick: () => void;
	onAdditionalDetailsClick: () => void;
	onUnableToProceedClick: () => void;
	onBackClick: () => void;
	careTeamZCClick: () => void;
	careTeamApproverClick: () => void;
	onViewAnswersClick: () => void;
	onViewInterviewCandidateQuestionsClick: () => void;
	onViewInterviewFamilyMemberClick: () => void;
	onViewInterviewPreceptorClick: () => void;
	onViewInterviewOtherReferenceClick: () => void;
	onViewCareTeamAnswersClick: () => void;
	onViewCareTeamInterviewAnswersClick: () => void;
	onViewCareTeamFamilyMemberInterviewAnswersClick: () => void;
}

const SectionHeader = ({
	heading,
	onBackClick,
	isMobile,
}: {
	heading: string;
	onBackClick: () => void;
	isMobile: boolean;
}) => {
	const { t } = useTranslation();

	const backIcon = isMobile ? (
		<Icon src={LeftArrow} width={20} height={20} />
	) : (
		<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
	);

	const BackButton = isMobile ? (
		<div onClick={onBackClick}>{backIcon}</div>
	) : (
		<Button
			type="primary"
			onClick={onBackClick}
			icon={backIcon}
			className="absolute font-bold text-sm left-0"
		>
			{t('back')}
		</Button>
	);

	return (
		<div className="relative flex items-center justify-between mb-4 py-2">
			{BackButton}
			<Text className="flex-grow text-center font-sans font-bold text-lg text-secondary-black">
				{heading}
			</Text>
		</div>
	);
};

export const LandingPage = ({
	application,
	onInterviewCandidateClick,
	onInterviewFamilyMemberClick,
	onInterviewOtherReferenceClick,
	onInterviewPreceptorClick,
	onProposerRecommendationClick,
	onCareTeamRecommendationClick,
	onZCRecommendationClick,
	onApproverRecommendationClick,
	onReviewAnswersClick,
	onViewAnswersClick,
	onViewCareTeamAnswersClick,
	onViewCareTeamInterviewAnswersClick,
	onViewCareTeamFamilyMemberInterviewAnswersClick,
	onAdditionalDetailsClick,
	onUnableToProceedClick,
	onBackClick,
	careTeamZCClick,
	careTeamApproverClick,
	onViewInterviewCandidateQuestionsClick,
	onViewInterviewOtherReferenceClick,
	onViewInterviewFamilyMemberClick,
	onViewInterviewPreceptorClick,
}: LandingPageProps) => {
	const { t } = useTranslation();
	const { getTranslatedValue } = useApplicationStatusTranslation();
	const { selectedRole } = useUserSession();
	const { batchDetails } = useFunctionaryHome();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;

	const isAllInterviewsDone = [
		application?.applicantAnswersReviewed,
		application?.applicantInterviewDone,
		application?.preceptorInterviewDone,
		application?.familyMemberInterviewDone,
	].every(Boolean);

	const showFinalRecommendationToProposer =
		(application?.applicationStatus === ApplicationStatus.REJECTED ||
			application?.applicationStatus === ApplicationStatus.APPROVED) &&
		selectedRole === UserRole.ZC;

	const isAdmin = selectedRole === UserRole.SNP_ADMIN;

	const isProposer = selectedRole === UserRole.PROPOSER;

	const isScreeningAdmin = selectedRole === UserRole.SCREENING_ADMIN;

	const isZC = selectedRole === UserRole.ZC;

	const isExamAdmin = selectedRole === UserRole.EXAM_ADMIN;

	const isReadOnly = isApplicationReadOnly(
		selectedRole,
		batchDetails?.approverReviewEndDate
	);
	const isCareTeam = selectedRole === UserRole.CARE_TEAM;

	const isRoleNotAllowed = !ALLOWED_ROLES.includes(selectedRole as UserRole);

	const isSkippedToEnd = isProposerSkippedToEnd(
		application?.proposerRecommendation
	);

	const AdditionalDetailsSection = (
		<div
			className="flex font-sans justify-center gap-1 mb-1 items-center cursor-pointer"
			onClick={onAdditionalDetailsClick}
		>
			<Text className="font-bold text-sm text-primary-blue whitespace-nowrap">
				{t('additionalDetails')}
			</Text>
			<Icon src={RightArrowCircle} width={21} height={21} />
		</div>
	);

	const CareTeamInterviewSection = (
		<div className="mt-4">
			<InterviewSection
				buttonText={!isCareTeam ? 'view' : 'careTeamRecommendation'}
				description="provideCareTeamRecommendation"
				title="careTeamRecommendation"
				isCompleted={!isEmpty(application?.careTeamRecommendation)}
				onButtonClick={onCareTeamRecommendationClick}
				{...(!isCareTeam ? { skipModal: true } : {})}
			/>
		</div>
	);

	const ZCInterviewSection = (
		<div className="mt-4">
			<InterviewSection
				buttonText={!isZC ? 'view' : 'ZCRecommendation'}
				description="provideZCRecommendation"
				title="ZCRecommendation"
				isCompleted={!isEmpty(application?.zcRecommendation)}
				onButtonClick={onZCRecommendationClick}
				{...(!isZC ? { skipModal: true } : {})}
			/>
		</div>
	);

	const ViewApplicationSection = (
		<div className="mt-4">
			<InterviewSection
				buttonText={isReadOnly ? 'view' : 'viewApplication'}
				description="viewTheAnswersProvidedByCandidateWhileApplying"
				title="candidateApplication"
				isCompleted={true}
				onButtonClick={onViewAnswersClick}
				skipModal
			/>
		</div>
	);

	const ViewCareTeamApplicationSection = (
		<div className="mt-4">
			<InterviewSection
				buttonText={'view'}
				description="viewCandidateAnswers"
				title="candidateApplication"
				isCompleted={true}
				onButtonClick={onViewCareTeamAnswersClick}
				skipModal
			/>
		</div>
	);

	const ViewCareTeamApplicationInterviewSection = (
		<div className="mt-4">
			<InterviewSection
				buttonText={'view'}
				description="viewProposersFeedback"
				title="candidateInterview"
				isCompleted={true}
				onButtonClick={onViewCareTeamInterviewAnswersClick}
				skipModal
			/>
		</div>
	);

	const ViewCareTeamFamilyMemberInterviewSection = (
		<div className="mt-4">
			<InterviewSection
				buttonText={'view'}
				description="viewProposersFeedback"
				title="familyMemberInterview"
				isCompleted={true}
				onButtonClick={onViewCareTeamFamilyMemberInterviewAnswersClick}
				skipModal
			/>
		</div>
	);

	const ViewCareTeamZCSection = (
		<div className="mt-4">
			<InterviewSection
				buttonText={'view'}
				description="viewZCComments"
				title="ZCRecommendation"
				isCompleted={!isEmpty(application?.zcRecommendation)}
				onButtonClick={careTeamZCClick}
				{...{ skipModal: true }}
			/>
		</div>
	);

	const ViewCareTeamApproverSection = (
		<div className="mt-4">
			<InterviewSection
				buttonText={'view'}
				description="viewRCComments"
				title="regionalCommitteeDecision"
				isCompleted={!isEmpty(application?.zcRecommendation)}
				onButtonClick={careTeamApproverClick}
				{...{ skipModal: true }}
			/>
		</div>
	);

	const ReferenceInterviewSection = (
		<>
			<div className="mt-4">
				<InterviewSection
					buttonText={isRoleNotAllowed ? 'view' : 'reviewApplication'}
					description="reviewTheAnswersProvidedByCandidateWhileApplying"
					title="candidateApplicationReview"
					isCompleted={application?.applicantAnswersReviewed}
					onButtonClick={onReviewAnswersClick}
					skipModal
				/>
			</div>
			{(!isProposer || application?.applicantAnswersReviewed) && (
				<>
					<div className="mt-4">
						<InterviewSection
							buttonText={isRoleNotAllowed ? 'view' : 'interviewCandidate'}
							description="talkToCandidateAndInterview"
							title="candidateInterview"
							isCompleted={application?.applicantInterviewDone}
							onButtonClick={onInterviewCandidateClick}
							{...(isRoleNotAllowed ? { skipModal: true } : {})}
							showDualButtons={
								isProposer && !application?.applicantInterviewDone
							}
							onViewQuestionClick={onViewInterviewCandidateQuestionsClick}
						/>
					</div>
					{application?.preceptorReference && (
						<div className="mt-4">
							<InterviewSection
								buttonText={isRoleNotAllowed ? 'view' : 'interviewPreceptor'}
								description="talkToCandidatePreceptor"
								title="candidatePreceptorInterview"
								isCompleted={application?.preceptorInterviewDone}
								onButtonClick={onInterviewPreceptorClick}
								{...(isRoleNotAllowed ? { skipModal: true } : {})}
								showDualButtons={
									isProposer && !application?.preceptorInterviewDone
								}
								onViewQuestionClick={onViewInterviewPreceptorClick}
							/>
						</div>
					)}
					{application?.familyMemberReference && (
						<div className="mt-4">
							<InterviewSection
								buttonText={isRoleNotAllowed ? 'view' : 'interviewFamilyMember'}
								description="talkToCandidateFamilyMember"
								title="familyMemberInterview"
								isCompleted={application?.familyMemberInterviewDone}
								onButtonClick={onInterviewFamilyMemberClick}
								{...(isRoleNotAllowed ? { skipModal: true } : {})}
								showDualButtons={
									isProposer && !application?.familyMemberInterviewDone
								}
								onViewQuestionClick={onViewInterviewFamilyMemberClick}
							/>
						</div>
					)}
					{application?.otherReferenceProvided && (
						<div className="mt-4">
							<InterviewSection
								buttonText={
									isRoleNotAllowed ? 'view' : 'interviewOtherReference'
								}
								description="talkToOtherReference"
								title="OtherReferenceInterview"
								isCompleted={application?.otherReferenceInterviewDone}
								onButtonClick={onInterviewOtherReferenceClick}
								{...(isRoleNotAllowed ? { skipModal: true } : {})}
								showDualButtons={
									isProposer && !application?.otherReferenceInterviewDone
								}
								onViewQuestionClick={onViewInterviewOtherReferenceClick}
							/>
						</div>
					)}
				</>
			)}
		</>
	);
	const ProposerRecommendationSection = (
		<div className="mt-4">
			<InterviewSection
				buttonText={!isProposer ? 'view' : 'proposerRecommendation'}
				description="provideProposerRecommendation"
				title="proposerRecommendation"
				isCompleted={!isEmpty(application?.proposerRecommendation)}
				onButtonClick={onProposerRecommendationClick}
				{...(!isProposer ? { skipModal: true } : {})}
			/>
		</div>
	);

	const ProposerSection = (
		<>
			<div className="sm:hidden mt-6">{AdditionalDetailsSection}</div>
			{ReferenceInterviewSection}
			{isAllInterviewsDone && <>{ProposerRecommendationSection}</>}
		</>
	);

	const ApproverSection = (
		<>
			{!isEmpty(application?.proposerRecommendation) && (
				<div className="mt-4">
					<InterviewSection
						buttonText={
							isAdmin || isScreeningAdmin || isExamAdmin
								? 'view'
								: isReadOnly && !isEmpty(application?.approverRecommendation)
								? 'view'
								: 'regionalCommitteeDecision'
						}
						description="provideRegionalCommitteeDecision"
						title="regionalCommitteeDecision"
						isCompleted={!isEmpty(application?.approverRecommendation)}
						onButtonClick={onApproverRecommendationClick}
						{...(isReadOnly && !isEmpty(application?.approverRecommendation)
							? { skipModal: true }
							: {})}
					/>
				</div>
			)}
		</>
	);

	const ZCSection = showFinalRecommendationToProposer ? (
		<>
			{(application?.shouldCareTeamReview ||
				!isEmpty(application?.careTeamRecommendation)) && (
				<div className="mt-4">
					<InterviewSection
						buttonText="careTeamRecommendation"
						description="provideCareTeamRecommendation"
						title="careTeamRecommendation"
						isCompleted={!isEmpty(application?.careTeamRecommendation)}
						onButtonClick={onCareTeamRecommendationClick}
					/>
				</div>
			)}
			<div className="mt-4">
				<InterviewSection
					buttonText="regionalCommitteeDecision"
					description="provideRegionalCommitteeDecision"
					title="regionalCommitteeDecision"
					isCompleted={!isEmpty(application?.approverRecommendation)}
					onButtonClick={onApproverRecommendationClick}
				/>
			</div>
		</>
	) : (
		<>
			<div className="sm:hidden mt-6">{AdditionalDetailsSection}</div>
			{ViewApplicationSection}
			{ZCInterviewSection}
		</>
	);

	const forwardedToCareTeamBy = [
		{ key: 'applicantForwardedToCareTeam', label: 'applicant' },
		{ key: 'proposerForwardedToCareTeam', label: 'proposer' },
		{ key: 'approverForwardedToCareTeam', label: 'approver' },
		{ key: 'zcForwardedToCareTeam', label: 'zc' },
	]
		.filter(({ key }) => application?.[key as keyof typeof application])
		.map(({ label }) => t(label));

	const CareTeamSection = (
		<>
			<div className="sm:hidden mt-6">{AdditionalDetailsSection}</div>
			<div
				className={`flex gap-20 justify-center items-center rounded-md py-4 px-8 border border-solid border-gray-200 ${
					isMobile ? '' : 'w-[736px]'
				}`}
			>
				<Text className="font-bold text-sm text-gray-650">
					{t('forwardedToCareTeamBy')}:{' '}
					{forwardedToCareTeamBy.length
						? forwardedToCareTeamBy.join(', ')
						: '-'}
				</Text>
			</div>
			{ViewCareTeamApplicationSection}
			{application.proposerForwardedToCareTeam &&
				application.applicantInterviewDone && (
					<>{ViewCareTeamApplicationInterviewSection}</>
				)}
			{application.proposerForwardedToCareTeam &&
				application.familyMemberInterviewDone && (
					<>{ViewCareTeamFamilyMemberInterviewSection}</>
				)}
			{application.zcForwardedToCareTeam &&
				!isEmpty(application?.zcRecommendation) && <>{ViewCareTeamZCSection}</>}
			{application.approverForwardedToCareTeam &&
				!isEmpty(application?.approverRecommendation) && (
					<>{ViewCareTeamApproverSection}</>
				)}
			{CareTeamInterviewSection}
		</>
	);

	const skippedApproverSection = (
		<>
			<div className="sm:hidden mt-6">{AdditionalDetailsSection}</div>
			{isSkippedToEnd ? (
				<>
					<div
						className={`flex gap-20 justify-center items-center rounded-md py-4 px-8 border border-solid border-gray-200 ${
							isMobile ? '' : 'w-[736px]'
						}`}
					>
						<Text className="font-bold text-sm text-gray-650">
							{t('proposerHasSkippedTheInterview')}
						</Text>
					</div>
					{ViewApplicationSection}
				</>
			) : (
				ReferenceInterviewSection
			)}
			{ApproverSection}
		</>
	);

	const unSkippedApproverSection = (
		<>
			<div className="sm:hidden mt-6">{AdditionalDetailsSection}</div>
			{ReferenceInterviewSection}
			{ProposerRecommendationSection}
			{ZCInterviewSection}
			{CareTeamInterviewSection}
			{ApproverSection}
		</>
	);

	let Body;
	if (selectedRole === UserRole.CARE_TEAM) {
		Body = CareTeamSection;
	} else if (selectedRole === UserRole.ZC) {
		Body = ZCSection;
	} else if (
		selectedRole &&
		[UserRole.APPROVER, UserRole.SNP_ADMIN, UserRole.SCREENING_ADMIN].includes(
			selectedRole
		)
	) {
		Body = isSkippedToEnd ? skippedApproverSection : unSkippedApproverSection;
	} else {
		Body = ProposerSection;
	}

	const showSkipToEnd =
		selectedRole &&
		[UserRole.PROPOSER, UserRole.SNP_ADMIN].includes(selectedRole) &&
		!application?.isApprover &&
		application?.applicationStatus === ApplicationStatus.FUNCTIONARY_ASSIGNED;

	return (
		<div className="flex justify-center">
			<div
				className={`flex flex-col h-full min-h-0 p-[8px] ${
					isMobile ? 'w-full' : 'w-[90%]'
				}`}
			>
				<SectionHeader
					heading={t('evaluate')}
					onBackClick={onBackClick}
					isMobile={isMobile}
				/>
				<div className={`${isMobile ? 'text-center' : 'flex justify-between'}`}>
					<div
						className={`my-2 font-sans font-semibold text-gray-650 ${
							!isCareTeam ? '' : 'invisible'
						}`}
					>
						{getTranslatedValue(application?.applicationStatus)}
					</div>
					{!isMobile && AdditionalDetailsSection}
				</div>

				<ApplicantDetailsTable applicantInfo={application.applicant} />
				<div className={`${isMobile ? '' : 'justify-items-center'}`}>
					<div
						className={`${
							isMobile
								? ''
								: 'bg-white w-fit mt-8 rounded-md px-12 pt-8 pb-4 flex flex-col items-center border border-solid border-gray-200'
						}`}
					>
						{Body}
						{showSkipToEnd && (
							<div className="mt-[16px] flex justify-center">
								<Button
									className="text-primary-blue font-bold text-sm"
									type="link"
									onClick={onUnableToProceedClick}
								>
									{t('unableToProceed')}
								</Button>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export const LandingPageContainer = () => {
	const { application, navigateToPage } = useInterviewLandingPage();
	const { navigateToRoute } = useBatchId();

	const location = useLocation();
	const state = location.state as LocationState | null;

	const evaluateFromDashboard =
		state?.from ?? sessionStorage.getItem('interviewOrigin');

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalTitle, setModalTitle] = useState<string | undefined>();
	const [comments, setComments] = useState<string | undefined>();

	type NavigationKeys =
		| 'onReviewAnswersClick'
		| 'onViewAnswersClick'
		| 'onInterviewCandidateClick'
		| 'onInterviewPreceptorClick'
		| 'onInterviewFamilyMemberClick'
		| 'onInterviewOtherReferenceClick'
		| 'onProposerRecommendationClick'
		| 'onCareTeamRecommendationClick'
		| 'onZCRecommendationClick'
		| 'onApproverRecommendationClick'
		| 'onAdditionalDetailsClick'
		| 'onUnableToProceedClick'
		| 'onViewInterviewCandidateQuestionsClick'
		| 'onViewInterviewOtherReferenceClick'
		| 'onViewInterviewFamilyMemberClick'
		| 'onViewInterviewPreceptorClick'
		| 'onViewCareTeamAnswersClick'
		| 'onViewCareTeamInterviewAnswersClick'
		| 'onViewCareTeamFamilyMemberInterviewAnswersClick';

	const navigationMap: Record<NavigationKeys, string> = {
		onReviewAnswersClick: ANSWERS_REVIEW_CONTEXT,
		onViewAnswersClick: VIEW_ANSWERS,
		onInterviewCandidateClick: APPLICANT_CONTEXT,
		onInterviewPreceptorClick: PRECEPTOR_CONTEXT,
		onInterviewFamilyMemberClick: FAMILY_CONTEXT,
		onInterviewOtherReferenceClick: OTHER_REFERENCE_CONTEXT,
		onProposerRecommendationClick: PROPOSER_RECOMMENDATION,
		onCareTeamRecommendationClick: CARE_TEAM_RECOMMENDATION,
		onZCRecommendationClick: ZC_RECOMMENDATION,
		onApproverRecommendationClick: APPROVER_RECOMMENDATION,
		onAdditionalDetailsClick: APPLICANT_ADDITIONAL_DETAILS,
		onUnableToProceedClick: SKIP_ALL_INTERVIEWS,
		onViewInterviewCandidateQuestionsClick: VIEW_APPLICANT_INTERVIEW,
		onViewInterviewOtherReferenceClick: VIEW_OTHER_REFERENCE_INTERVIEW,
		onViewInterviewFamilyMemberClick: VIEW_FAMILY_MEMBER_INTERVIEW,
		onViewInterviewPreceptorClick: VIEW_PRECEPTOR_INTERVIEW,
		onViewCareTeamAnswersClick: VIEW_CARE_TEAM_APPLICANT_ANSWERS,
		onViewCareTeamInterviewAnswersClick:
			VIEW_CARE_TEAM_APPLICANT_INTERVIEW_ANSWERS,
		onViewCareTeamFamilyMemberInterviewAnswersClick:
			VIEW_CARE_TEAM_FAMILY_ANSWERS,
	};

	// Reduce with exact keys of LandingPageProps
	const landingPageHandlers: Pick<LandingPageProps, NavigationKeys> =
		Object.entries(navigationMap).reduce((acc, [key, route]) => {
			acc[key as NavigationKeys] = () => navigateToPage(route);
			return acc;
		}, {} as Pick<LandingPageProps, NavigationKeys>);

	const handleBackClick = useCallback(() => {
		if (evaluateFromDashboard === FROM_SCREENING_DASHBOARD) {
			navigateToRoute(SCREENING_DASHBOARD);
			sessionStorage.removeItem('interviewOrigin');
		} else if (evaluateFromDashboard === FROM_CTD) {
			navigateToRoute(CENTRAL_TEAM_APPLICATIONS);
			sessionStorage.removeItem('interviewOrigin');
		} else {
			resetApplicationsListCache();
			navigateToRoute(FUNCTIONARY_HOME);
		}
	}, [evaluateFromDashboard, navigateToRoute]);

	const handleViewCareTeamZCClick = useCallback(() => {
		setModalTitle('ZCRecommendation');
		setComments(application?.zcRecommendation?.comments);
		setIsModalOpen(true);
	}, [application?.zcRecommendation?.comments]);

	const handleViewCareTeamApproverClick = useCallback(() => {
		setModalTitle('rcRecommendation');
		setComments(application?.approverRecommendation?.comments);
		setIsModalOpen(true);
	}, [application?.approverRecommendation?.comments]);

	return (
		<>
			<LandingPage
				application={application}
				onBackClick={handleBackClick}
				careTeamZCClick={handleViewCareTeamZCClick}
				careTeamApproverClick={handleViewCareTeamApproverClick}
				{...landingPageHandlers}
			/>

			<CommentsModal
				visible={isModalOpen}
				title={modalTitle}
				text={comments}
				onClose={() => setIsModalOpen(false)}
			/>
		</>
	);
};
